import React, { useMemo, useState, useEffect } from "react";
import {
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Grid,
  Link,
  Divider,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { decoratedFetch } from "../req_utils";
import { useSelector, useDispatch } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { updateProject } from "../reducers";
import ProjectDetails from "../components/ProjectDetails";
import FileTab from "../components/FileTab";
import LogTab from "./LogTab";
import ContactsTab from "./ContactsTab";
import AddressesTab from "./AddressesTab";
import ThemedTabs from "../components/ThemedTabs";
import ThemedTypography from "../components/ThemedTypography";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import AccountNotesTab from "./AccountNotesTab";
import FinancialInfoTab from "./FinancialInfoTab";

dayjs.extend(utc);

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 0.5, md: 1 } }}>
          <ThemedTypography>{children}</ThemedTypography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const ProjectDetailsTabs = ({ projectData }) => {
  const dispatchState = useDispatch();
  const project = useSelector((state) => state.project);
  const employee_branch_id = useSelector((state) => state.employee_branch_id);
  const accountId = useSelector((state) => state.project?.account?.id);
  const [employees, setEmployees] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [branchAddresses, setBranchAddresses] = useState([]);
  const [branchAddressId, setBranchAddressId] = useState("");
  const [measuredById, setMeasuredById] = useState("");

  const theme = useTheme();
  const isBelowMdScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isBelowLgScreen = useMediaQuery(theme.breakpoints.down("lg"));

  const dateTheme = useMemo(
    () =>
      createTheme({
        components: {
          MuiInputLabel: {
            styleOverrides: {
              root: {
                marginTop: project?.start_date ? "0px" : "-7px",
              },
            },
          },
        },
      }),
    [project],
  );

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleBranchBlur = () => {
    dispatchState(
      updateProject({
        ...project,
        branch: branchAddresses.filter(
          (branchAddress) => branchAddress.id === branchAddressId,
        )[0],
      }),
    );
  };

  const handleMeasuredByBlur = () => {
    dispatchState(
      updateProject({
        ...project,
        measured_by_employee: employees.filter(
          (employee) => employee.id === measuredById,
        )[0],
      }),
    );
  };

  useEffect(() => {
    setBranchAddressId(project?.branch?.id || "");
    setMeasuredById(project?.measured_by_employee?.id || "");
  }, [project]);

  useEffect(() => {
    if (tabValue !== 0) {
      // If project info tab is not selected
      return;
    }
    if (!employee_branch_id) return;
    
    decoratedFetch(`/list_employees?branch_id=${employee_branch_id}`)
      .then((response) => response.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setEmployees(data);
        } else {
          setEmployees([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
        // Set an empty array in case of an error
        setEmployees([]);
      });
    decoratedFetch("/list_branch_addresses")
      .then((response) => response.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setBranchAddresses(data);
        } else {
          setBranchAddresses([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
        setBranchAddresses([]);
      });
  }, [tabValue, employee_branch_id]);

  const updateProjectSR = (event) => {
    const newSalesRepId = event.target.value;
    if (newSalesRepId === project.sales_employee.id) {
      return;
    }
    const updatedProject = {
      ...project,
      sales_employee: employees.find(
        (employee) => employee.id === newSalesRepId,
      ),
    };
    dispatchState(updateProject(updatedProject));
  };

  const updateProjectPM = (event) => {
    const newPMId = event.target.value;
    if (newPMId === project.pm_employee.id) {
      return;
    }
    const updatedProject = {
      ...project,
      pm_employee: employees.find((employee) => employee.id === newPMId),
    };
    dispatchState(updateProject(updatedProject));
  };

  let tabLabels = [];
  if (isBelowLgScreen) {
    if (isBelowMdScreen) {
      tabLabels = [
        "Project Details",
        "Log",
        "Files",
        "Contacts",
        "Addresses",
        "Account Notes",
        "Financial Info",
      ];
    } else {
      tabLabels = [
        "Project Details",
        "Log",
        "Files",
        "Contacts",
        "Addresses",
        "Account Notes",
        "Financial Info",
      ];
    }
  } else {
    tabLabels = ["Project Details", "Log", "Files", "Financial Info"];
  }

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <ThemedTabs
            value={tabValue}
            labels={tabLabels}
            onChange={handleTabChange}
            variant="fullWidth"
          />
        </Box>
        <TabPanel value={tabValue} index={0}>
          <ProjectDetails />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <LogTab
            value={tabValue}
            index={1}
            projectName={project.name}
            projectCustomId={project.custom_id}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <FileTab value={tabValue} index={2} />
        </TabPanel>
        {!isBelowLgScreen && (
          <TabPanel value={tabValue} index={3}>
            <FinancialInfoTab
              value={tabValue}
              index={3}
              projectId={project?.id}
              accountId={accountId}
            />
          </TabPanel>
        )}
        {isBelowLgScreen ? (
          <>
            <TabPanel value={tabValue} index={3}>
              <ContactsTab value={tabValue} index={3} account_id={accountId} />
            </TabPanel>
            <TabPanel value={tabValue} index={4}>
              <AddressesTab value={tabValue} index={4} account_id={accountId} />
            </TabPanel>
            <TabPanel value={tabValue} index={5}>
              <AccountNotesTab />
            </TabPanel>
            <TabPanel value={tabValue} index={6}>
              <FinancialInfoTab
                value={tabValue}
                index={6}
                projectId={project?.id}
                accountId={accountId}
              />
            </TabPanel>
          </>
        ) : null}
      </Box>
    </>
  );
};

export default ProjectDetailsTabs;
